import styled from "@emotion/styled";
import type { Theme } from "@mui/material";
import {
  type PlateEditor,
  type PlateRenderElementProps,
  type TElement,
  type Value,
  createPluginFactory,
} from "@udecode/plate";
import { ELEMENT_BANNER } from "../elements";

export const Banner = { GREEN: "green", YELLOW: "yellow", RED: "red", GRAY: "gray" } as const;
export type Banner = (typeof Banner)[keyof typeof Banner];
export interface TBanner extends TElement {
  type: typeof ELEMENT_BANNER;
  banner: Banner;
}

export const getBannerColour = (banner: Banner, theme: Theme) => {
  switch (banner) {
    case Banner.GRAY:
      return "var(--border)";
    case Banner.GREEN:
      return "var(--success)";
    case Banner.YELLOW:
      return "var(--warning)";
    case Banner.RED:
      return "var(--destructive)";
  }
};

const BannerContainer = styled.div`
  position: relative;

  ${Object.values(Banner)
    .map(
      (b) => `
        &[data-banner='${b}'] + &[data-banner='${b}'] {
          margin-top: calc(-1rem - 2px);

          .banner {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
          }
        }
      `,
    )
    .join("\n")}

  &[data-banner="green"] {
    --banner-color: var(--success-light);
    --banner-border: var(--success);
  }
  &[data-banner="yellow"] {
    --banner-color: var(--warning-light);
    --banner-border: var(--warning);
  }
  &[data-banner="red"] {
    --banner-color: var(--destructive-light);
    --banner-border: var(--destructive);
  }

  &[data-banner="gray"] {
    --banner-color: var(--subtle-light);
    --banner-border: var(--border);
  }

  > .banner {
    padding: 0.5rem 1rem;
    background-color: var(--banner-color);
    border-radius: 0.125rem 0.25rem 0.25rem 0.125rem;
    border-left: 2px solid var(--banner-border);
  }
`;

export const BannerRenderElement = ({ attributes, children, element }: PlateRenderElementProps<Value, TBanner>) => {
  const banner = element.banner ?? Banner.GREEN;

  return (
    <BannerContainer
      {...attributes}
      data-banner={banner}
    >
      <div className="banner">{children}</div>
    </BannerContainer>
  );
};

export const createBannerPlugin = createPluginFactory({
  key: ELEMENT_BANNER,
  isElement: true,
  component: BannerRenderElement,
  withOverrides: (editor: PlateEditor) => {
    const { normalizeNode } = editor;
    editor.normalizeNode = ([node, path]) => {
      if (node.type === ELEMENT_BANNER) {
        if (!node.banner) {
          node.banner = Banner.GREEN;
          return;
        }
      }
      return normalizeNode([node, path]);
    };
    return editor;
  },
});
